import { ReactElement, ReactNode } from 'react';
import { File06 } from '../../../assets';
import { ReportType } from '../../../enums/ReportType';
import { Role } from '../../../enums/Role';
import ActualVsTheoryReportForm from '../forms/ActualVsTheoryReportForm';
import StockOnHandReportForm from '../forms/StockOnHandReportForm';
import WasteReportForm from '../forms/WasteReportForm';
import ItemActivityReportForm from '../forms/ItemActivityReportForm';
import TransfersReportForm from '../forms/TransfersReportForm';
import ExtendedStockCountReportForm from '../forms/ExtendedStockCountReportForm';
import StockCountSummaryReportForm from '../forms/StockCountSummaryReportForm';
import ItemTimeoutReportForm from '../forms/ItemTimeoutReportForm';
import CostOfSalesReportForm from '../forms/CostOfSalesReportForm';
import StockDaysReportForm from '../forms/StockDaysReportForm';
import InvoiceReportForm from '../forms/InvoiceReportForm';
import MarginReportForm from '../forms/MarginReportForm';
import CreditPurchaseReportForm from '../forms/CreditPurchaseReportForm';
import StockCountVsUsageReportForm from '../forms/StockCountVsUsageReportForm';
import DcShortageReportForm from '../forms/DcShortageReportForm';
import ConstrainedItemReportForm from '../forms/ConstrainedItemReportForm';
import ItemStockOutReportForm from '../forms/ItemStockOutReportForm';
import ReceiptsReportForm from '../forms/ReceiptsReportForm';
import MadeInStoreReportForm from '../forms/MadeInStoreReportForm';
import CogsReportForm from '../forms/CogsReportForm';
import SalesCheckReportForm from '../forms/SalesCheckReportForm';
import ItemUsageReportForm from '../forms/ItemUsageReportForm';
import InventoryExtensionReportForm from '../forms/InventoryExtensionReportForm';
import GiveawaysSamplesReportForm from '../forms/GiveawaysSamplesReportForm';
import ForecastOrdersReportForm from '../forms/ForecastOrdersReportForm';
import DeliveryReportForm from '../forms/DeliveryReportForm';
import OutstandingDeliveriesReportForm from '../forms/OutstandingDeliveriesReportForm';
import UserReportForm from '../forms/UserReportForm';

export interface ReportNavItem {
  title: ReportType,
  icon: ReactNode,
  info: string,
  form: ReactElement,
  permission: Role,
  // Reports V2 grouping. Optional so the legacy page is unaffected.
  category?: string,
}

export const allReportPermissions: Role[] = [
  'ReportActualVsTheoryRead', 'ReportStockCountVsUsageRead', 'ReportTransfersRead',
  'ReportStockOnHandRead', 'ReportWasteRead', 'ReportItemActivityRead',
  'ReportItemTimeoutRead', 'ReportCostOfSalesRead', 'ReportStockDaysRead',
  'ReportInvoiceRead', 'ReportStockCountSummaryRead', 'ReportMarginRead',
  'ReportCreditPurchaseRead', 'ReportExtendedStockCountRead', 'ReportDcShortageRead',
  'ReportConstrainedItemRead', 'ReportItemStockOutRead', 'ReportReceiptRead',
  'ReportMadeInStoreRead', 'ReportCogsRead', 'ReportSalesCheckRead',
  'ReportItemUsageRead', 'ReportInventoryExtensionRead', 'ReportGiveawaysSamplesRead',
  'ReportForecastOrdersRead', 'ReportDeliveryRead', 'ReportOutstandingDeliveriesRead',
  'ReportUserRead',
];

export const ReportTypesData: ReportNavItem[] = [
  {
    title: ReportType.ActualVsTheory,
    icon: <File06 />,
    info: 'reporting.report-descriptions.actual-vs-theorectical',
    form: <ActualVsTheoryReportForm />,
    permission: 'ReportActualVsTheoryRead',
  },
  {
    title: ReportType.StockCountVsUsage,
    icon: <File06 />,
    info: 'reporting.report-descriptions.stockcountsvsusage',
    form: <StockCountVsUsageReportForm />,
    permission: 'ReportStockCountVsUsageRead',
  },
  {
    title: ReportType.Transfers,
    icon: <File06 />,
    info: 'reporting.report-descriptions.transfers',
    form: <TransfersReportForm />,
    permission: 'ReportTransfersRead',
  },
  {
    title: ReportType.StockOnHand,
    icon: <File06 />,
    info: 'reporting.report-descriptions.stock-on-hand',
    form: <StockOnHandReportForm />,
    permission: 'ReportStockOnHandRead',
  },
  {
    title: ReportType.Waste,
    icon: <File06 />,
    info: 'reporting.report-descriptions.waste',
    form: <WasteReportForm />,
    permission: 'ReportWasteRead',
  },
  {
    title: ReportType.ItemActivity,
    icon: <File06 />,
    info: 'reporting.report-descriptions.item-activity',
    form: <ItemActivityReportForm />,
    permission: 'ReportItemActivityRead',
  },
  {
    title: ReportType.ItemTimeout,
    icon: <File06 />,
    info: 'reporting.report-descriptions.item-time-out',
    form: <ItemTimeoutReportForm />,
    permission: 'ReportItemTimeoutRead',
  },
  {
    title: ReportType.CostOfSales,
    icon: <File06 />,
    info: 'reporting.report-descriptions.cost-of-sales',
    form: <CostOfSalesReportForm />,
    permission: 'ReportCostOfSalesRead',
  },
  {
    title: ReportType.StockDays,
    icon: <File06 />,
    info: 'reporting.report-descriptions.stock-days',
    form: <StockDaysReportForm />,
    permission: 'ReportStockDaysRead',
  },
  {
    title: ReportType.Invoice,
    icon: <File06 />,
    info: 'reporting.report-descriptions.invoice',
    form: <InvoiceReportForm />,
    permission: 'ReportInvoiceRead',
  },
  {
    title: ReportType.StockCountSummary,
    icon: <File06 />,
    info: 'reporting.report-descriptions.stockcountsummary',
    form: <StockCountSummaryReportForm />,
    permission: 'ReportStockCountSummaryRead',
  },
  {
    title: ReportType.Margin,
    icon: <File06 />,
    info: 'reporting.report-descriptions.margin',
    form: <MarginReportForm />,
    permission: 'ReportMarginRead',
  },
  {
    title: ReportType.CreditPurchase,
    icon: <File06 />,
    info: 'reporting.report-descriptions.credit-purchase-report',
    form: <CreditPurchaseReportForm />,
    permission: 'ReportCreditPurchaseRead',
  },
  {
    title: ReportType.ExtendedStockCount,
    icon: <File06 />,
    info: 'reporting.report-descriptions.extendedstockcount',
    form: <ExtendedStockCountReportForm />,
    permission: 'ReportExtendedStockCountRead',
  },
  {
    title: ReportType.DcShortage,
    icon: <File06 />,
    info: 'reporting.report-descriptions.dc-shortage',
    form: <DcShortageReportForm />,
    permission: 'ReportDcShortageRead',
  },
  {
    title: ReportType.ConstrainedItem,
    icon: <File06 />,
    info: 'reporting.report-descriptions.constrained-item',
    form: <ConstrainedItemReportForm />,
    permission: 'ReportConstrainedItemRead',
  },
  {
    title: ReportType.ItemStockOut,
    icon: <File06 />,
    info: 'reporting.report-descriptions.item-stock-out',
    form: <ItemStockOutReportForm />,
    permission: 'ReportItemStockOutRead',
  },
  {
    title: ReportType.Receipt,
    icon: <File06 />,
    info: 'reporting.report-descriptions.receipt',
    form: <ReceiptsReportForm />,
    permission: 'ReportReceiptRead',
  },
  {
    title: ReportType.MadeInStore,
    icon: <File06 />,
    info: 'reporting.report-descriptions.made-in-store',
    form: <MadeInStoreReportForm />,
    permission: 'ReportMadeInStoreRead',
  },
  {
    title: ReportType.Cogs,
    icon: <File06 />,
    info: 'reporting.report-descriptions.cogs',
    form: <CogsReportForm />,
    permission: 'ReportCogsRead',
  },
  {
    title: ReportType.SalesCheck,
    icon: <File06 />,
    info: 'reporting.report-descriptions.sales-check',
    form: <SalesCheckReportForm />,
    permission: 'ReportSalesCheckRead',
  },
  {
    title: ReportType.ItemUsage,
    icon: <File06 />,
    info: 'reporting.report-descriptions.item-usage',
    form: <ItemUsageReportForm />,
    permission: 'ReportItemUsageRead',
  },
  {
    title: ReportType.inventoryExtension,
    icon: <File06 />,
    info: 'reporting.report-descriptions.inventoryExtension',
    form: <InventoryExtensionReportForm />,
    permission: 'ReportInventoryExtensionRead',
  },
  {
    title: ReportType.giveawaysSamples,
    icon: <File06 />,
    info: 'reporting.report-descriptions.giveawaySamples',
    form: <GiveawaysSamplesReportForm />,
    permission: 'ReportGiveawaysSamplesRead',
  },
  {
    title: ReportType.ForecastOrders,
    icon: <File06 />,
    info: 'reporting.report-descriptions.forecast-orders',
    form: <ForecastOrdersReportForm />,
    permission: 'ReportForecastOrdersRead',
  },
  {
    title: ReportType.Delivery,
    icon: <File06 />,
    info: 'reporting.report-descriptions.delivery',
    form: <DeliveryReportForm />,
    permission: 'ReportDeliveryRead',
  },
  {
    title: ReportType.OutstandingDeliveries,
    icon: <File06 />,
    info: 'reporting.report-descriptions.outstanding-deliveries',
    form: <OutstandingDeliveriesReportForm />,
    permission: 'ReportOutstandingDeliveriesRead',
  },
  {
    title: ReportType.UserReport,
    icon: <File06 />,
    info: 'reporting.report-descriptions.user-report',
    form: <UserReportForm />,
    permission: 'ReportUserRead',
  },
];